import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchCatalogEvents, type CatalogEvent, type OrgBrandingInfo } from '../../api/learnPublicApi';
import TrainingTopbar, { useOrgBranding } from './TrainingTopbar';
import {
  MagnifyingGlass, MapPin, User,
  WarningCircle, Tray, CalendarBlank,
  ArrowRight, CaretDown, Check, SortAscending,
  Monitor, Buildings, Broadcast,
  Clock, Users as UsersIcon, Lightning, X,
} from '@phosphor-icons/react';

/* ─────────────────────────── constants ─────────────────────────── */

const GRADIENTS = [
  'from-[#4B45E5]/10 to-[#4B45E5]/3',
  'from-emerald-500/10 to-emerald-500/3',
  'from-amber-500/10 to-amber-500/3',
  'from-rose-500/10 to-rose-500/3',
  'from-cyan-500/10 to-cyan-500/3',
  'from-violet-500/10 to-violet-500/3',
];
const DOTS = ['bg-[#4B45E5]', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-violet-500'];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return Math.abs(h);
}

/* ─────────────────────────── helpers ─────────────────────────── */

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function modeLabel(mode: string): string {
  if (!mode) return 'Virtual';
  const m = mode.toLowerCase();
  if (m === 'onsite' || m === 'in_person' || m === 'in-person' || m === 'vilt') return 'Onsite';
  if (m === 'hybrid') return 'Hybrid';
  return 'Virtual';
}

function ModeIcon({ mode, size = 13 }: { mode: string; size?: number }) {
  const m = modeLabel(mode);
  if (m === 'Onsite') return <Buildings size={size} weight="bold" />;
  if (m === 'Hybrid') return <Broadcast size={size} weight="bold" />;
  return <Monitor size={size} weight="bold" />;
}

type TabKey = 'current' | 'past' | 'all';
type ModeFilter = 'all' | 'virtual' | 'onsite' | 'hybrid';
type SortKey = 'date_asc' | 'date_desc' | 'popular' | 'spots';

/* ─────────────── Skeleton Card (shimmer loading) ─────────────── */

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-24 bg-gray-100" />
      <div className="p-5 space-y-3">
        <div className="flex gap-2">
          <div className="h-5 w-16 bg-gray-100 rounded-md" />
          <div className="h-5 w-12 bg-gray-100 rounded-md" />
        </div>
        <div className="h-5 w-3/4 bg-gray-100 rounded-md" />
        <div className="h-4 w-full bg-gray-50 rounded-md" />
        <div className="flex justify-between pt-2">
          <div className="h-4 w-20 bg-gray-100 rounded-md" />
          <div className="h-4 w-16 bg-gray-100 rounded-md" />
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── Badge (per-event status) ──────────────────── */

function EventBadge({ event }: { event: CatalogEvent }) {
  const totalSpots = event.sessions?.reduce((a, s) => a + (s.available_spots || 0), 0) || 0;
  const totalMax = event.sessions?.reduce((a, s) => a + (s.max_participants || 0), 0) || 0;
  const fillPct = totalMax > 0 ? Math.round(((totalMax - totalSpots) / totalMax) * 100) : 0;

  const createdRecently = event.sessions?.[0]?.start_date &&
    (new Date().getTime() - new Date(event.sessions[0].start_date).getTime()) < 0 &&
    (new Date(event.sessions[0].start_date).getTime() - new Date().getTime()) > 7 * 86400000;

  // Priority: Full > Few Spots > Filling Fast > New
  if (totalMax > 0 && totalSpots === 0) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-500 border border-red-100">Full</span>;
  }
  if (totalMax > 0 && totalSpots <= 5 && totalSpots > 0) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-100">Few Spots</span>;
  }
  if (fillPct > 70) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-orange-50 text-orange-500 border border-orange-100">Filling Fast</span>;
  }
  if (createdRecently) {
    return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#4B45E51A] text-[#4B45E5] border border-[#4B45E5]/10">New</span>;
  }
  return null;
}

/* ──────────────────────── Event Card ──────────────────────── */

function EventCard({ event, isPast, onClick, index }: {
  event: CatalogEvent;
  isPast: boolean;
  onClick: () => void;
  index: number;
}) {
  const s0 = event.sessions?.[0];
  const sessionCount = event.sessions?.length || 0;
  const totalMax = event.sessions?.reduce((a, s) => a + (s.max_participants || 0), 0) || 0;
  const totalEnrolled = event.sessions?.reduce((a, s) => a + (s.enrolled_count || 0), 0) || 0;
  const totalSpots = event.sessions?.reduce((a, s) => a + (s.available_spots || 0), 0) || 0;
  const fillPct = totalMax > 0 ? Math.min(100, Math.round((totalEnrolled / totalMax) * 100)) : 0;
  const mode = s0 ? modeLabel(s0.mode) : 'Virtual';
  const h = hashStr(event.event_name);
  const gradient = GRADIENTS[h % GRADIENTS.length];
  const dot = DOTS[h % DOTS.length];

  let spotsLabel = '';
  let spotsColor = 'text-emerald-600';
  if (totalMax > 0) {
    if (totalSpots === 0) { spotsLabel = 'Full'; spotsColor = 'text-red-500'; }
    else if (fillPct > 80) { spotsLabel = `${totalSpots} left`; spotsColor = 'text-amber-600'; }
    else { spotsLabel = `${totalSpots} spots`; spotsColor = 'text-emerald-600'; }
  }

  return (
    <div
      onClick={onClick}
      className={`group relative bg-white rounded-xl border border-gray-200/80 cursor-pointer overflow-hidden
        transition-all duration-200 hover:shadow-[0_12px_40px_-16px_rgba(0,0,0,0.12)] hover:-translate-y-[2px] hover:border-gray-300/80
        ${isPast ? 'opacity-50 hover:opacity-75' : ''}
        animate-[fadeSlideUp_0.4s_ease-out_both]`}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Color banner */}
      <div className={`h-20 bg-gradient-to-br ${gradient} relative overflow-hidden`}>
        {/* Decorative dots */}
        <div className={`absolute top-3 right-3 w-8 h-8 rounded-full ${dot} opacity-[0.08]`} />
        <div className={`absolute bottom-2 right-8 w-5 h-5 rounded-full ${dot} opacity-[0.06]`} />
        {/* Mode + date overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-2.5 pt-6 bg-gradient-to-t from-white/80 to-transparent flex items-end justify-between">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/90 backdrop-blur-sm text-[11px] font-semibold text-gray-700 shadow-sm border border-gray-200/50">
            <ModeIcon mode={s0?.mode || ''} size={11} />
            {isPast ? 'Completed' : mode}
          </span>
          {s0?.start_date && (
            <span className="text-[11px] font-medium text-gray-500 tabular-nums bg-white/80 px-1.5 py-0.5 rounded">
              {formatDateShort(s0.start_date)}
            </span>
          )}
        </div>
      </div>

      <div className="p-4 pt-3">
        {/* Badge row */}
        <div className="flex items-center justify-between mb-2 min-h-[20px]">
          <EventBadge event={event} />
          {event.course_type && (
            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{event.course_type}</span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-semibold text-gray-900 leading-snug mb-1 line-clamp-2 group-hover:text-[#4B45E5] transition-colors">
          {event.event_name}
        </h3>

        {/* Description snippet */}
        {event.course_description && (
          <p className="text-xs text-gray-400 leading-relaxed line-clamp-2 mb-2">{event.course_description}</p>
        )}

        {/* Meta pills */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-2 pt-2.5 border-t border-gray-100">
          {s0?.location_name && (
            <span className="flex items-center gap-1 truncate max-w-[140px]">
              <MapPin size={11} className="text-gray-400 flex-shrink-0" /> {s0.location_name}
            </span>
          )}
          {s0?.trainer_name && (
            <span className="flex items-center gap-1 truncate max-w-[120px]">
              <User size={11} className="text-gray-400 flex-shrink-0" /> {s0.trainer_name}
            </span>
          )}
          <span className="flex items-center gap-1 flex-shrink-0">
            <CalendarBlank size={11} className="text-gray-400" />
            {sessionCount} session{sessionCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          {totalMax > 0 ? (
            <div className="flex items-center gap-2">
              <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    fillPct > 90 ? 'bg-red-400' : fillPct > 60 ? 'bg-amber-400' : 'bg-emerald-400'
                  }`}
                  style={{ width: `${fillPct}%` }}
                />
              </div>
              <span className={`text-[11px] font-semibold ${spotsColor}`}>{spotsLabel}</span>
            </div>
          ) : (
            <span className="text-[11px] text-gray-400">Open registration</span>
          )}

          {!isPast && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4B45E5] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              View <ArrowRight size={11} weight="bold" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── Filter Dropdown ──────────────────── */

function FilterDropdown<T extends string>({ label, icon: Icon, items, value, onChange }: {
  label: string;
  icon: React.ElementType;
  items: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeLabel = items.find((i) => i.key === value)?.label || label;
  const isDefault = items[0]?.key === value;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 pl-3 pr-2.5 py-2 text-sm font-medium rounded-lg border cursor-pointer outline-none transition-all
          ${isDefault
            ? 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            : 'bg-[#4B45E51A] border-[#4B45E5]/20 text-[#4B45E5]'}
          focus:border-[#4B45E5] focus:ring-2 focus:ring-[#4B45E5]/10`}
      >
        <Icon size={14} weight="bold" className={isDefault ? 'text-gray-400' : 'text-[#4B45E5]'} />
        <span>{activeLabel}</span>
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''} ${isDefault ? 'text-gray-400' : 'text-[#4B45E5]'}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 animate-[fadeIn_0.15s_ease-out]">
          {items.map((item) => {
            const selected = item.key === value;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => { onChange(item.key); setOpen(false); }}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                <span>{item.label}</span>
                {selected && <Check size={14} weight="bold" className="text-[#4B45E5]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Main Page ──────────────────────── */

export default function TrainingCatalogPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState<CatalogEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 12;

  const { subdomain, org: orgBranding, orgNotFound, orgLoading } = useOrgBranding();
  const orgFromQuery = searchParams.get('org');
  const orgDomain = subdomain || orgFromQuery || undefined;
  const [apiOrg, setApiOrg] = useState<OrgBrandingInfo | null>(null);

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [timeFilter, setTimeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortKey>('date_asc');

  useEffect(() => {
    if (subdomain && orgLoading) return;
    if (subdomain && orgNotFound) return;
    setLoading(true);
    setError(null);
    fetchCatalogEvents(pageSize, page * pageSize, orgDomain)
      .then((data) => {
        setEvents(data.events || []);
        setTotal(data.total || 0);
        if (data.organization) setApiOrg(data.organization);
      })
      .catch(() => setError('Failed to load training catalog'))
      .finally(() => setLoading(false));
  }, [page, orgDomain, orgLoading, orgNotFound, subdomain]);

  const filtered = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const result = events.filter((e) => {
      if (activeTab !== 'all') {
        const sessions = e.sessions || [];
        const hasUpcoming = sessions.length === 0 || sessions.some((s) => {
          const endDate = s.end_date || s.start_date;
          if (!endDate) return true;
          return new Date(endDate) >= now;
        });
        if (activeTab === 'current' && !hasUpcoming) return false;
        if (activeTab === 'past' && hasUpcoming) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!e.event_name?.toLowerCase().includes(q) && !e.course_title?.toLowerCase().includes(q) && !e.course_description?.toLowerCase().includes(q)) return false;
      }
      if (modeFilter !== 'all') {
        const modes = (e.sessions || []).map((s) => modeLabel(s.mode).toLowerCase());
        if (!modes.some((m) => m === modeFilter)) return false;
      }
      if (timeFilter !== 'all') {
        const tMonth = timeFilter === 'this_month' ? now.getMonth() : now.getMonth() + 1;
        const tYear = timeFilter === 'next_month' && now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
        const match = (e.sessions || []).some((s) => {
          if (!s.start_date) return false;
          const d = new Date(s.start_date);
          return d.getMonth() === tMonth % 12 && d.getFullYear() === tYear;
        });
        if (!match) return false;
      }
      return true;
    });

    // Sort
    result.sort((a, b) => {
      const aDate = a.sessions?.[0]?.start_date ? new Date(a.sessions[0].start_date).getTime() : 0;
      const bDate = b.sessions?.[0]?.start_date ? new Date(b.sessions[0].start_date).getTime() : 0;
      if (sortBy === 'date_asc') return aDate - bDate;
      if (sortBy === 'date_desc') return bDate - aDate;
      if (sortBy === 'popular') {
        const aEnrolled = a.sessions?.reduce((s, se) => s + (se.enrolled_count || 0), 0) || 0;
        const bEnrolled = b.sessions?.reduce((s, se) => s + (se.enrolled_count || 0), 0) || 0;
        return bEnrolled - aEnrolled;
      }
      if (sortBy === 'spots') {
        const aSpots = a.sessions?.reduce((s, se) => s + (se.available_spots || 0), 0) || 0;
        const bSpots = b.sessions?.reduce((s, se) => s + (se.available_spots || 0), 0) || 0;
        return bSpots - aSpots;
      }
      return 0;
    });

    return result;
  }, [events, search, modeFilter, timeFilter, activeTab, sortBy]);

  const isEventPast = (ev: CatalogEvent) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const sessions = ev.sessions || [];
    if (sessions.length === 0) return false;
    return sessions.every((s) => {
      const endDate = s.end_date || s.start_date;
      if (!endDate) return false;
      return new Date(endDate) < now;
    });
  };

  const totalPages = Math.ceil(total / pageSize);
  const effectiveOrg = orgBranding || apiOrg;
  const orgName = effectiveOrg?.company_name || orgDomain || 'Training';
  const totalSessions = events.reduce((s, e) => s + (e.sessions?.length || 0), 0);
  const totalSpots = events.reduce((s, e) => s + (e.sessions || []).reduce((a, sess) => a + (sess.available_spots || 0), 0), 0);
  const hasActiveFilters = modeFilter !== 'all' || timeFilter !== 'all' || search;

  // Override body overflow:hidden for standalone catalog page
  useEffect(() => {
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = ''; };
  }, []);

  /* ── Org Not Found ── */
  if (subdomain && orgNotFound && !orgLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
        <TrainingTopbar linkToCatalog={false} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <WarningCircle size={32} weight="fill" className="text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Organisation Not Found</h2>
            <p className="text-sm text-gray-500 mb-2 leading-relaxed">
              We couldn't find an organisation matching <span className="font-semibold text-gray-700">"{subdomain}"</span>.
            </p>
            <p className="text-sm text-gray-400 leading-relaxed">
              Check the URL or contact your training administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>

      {/* ═══ Header ═══ */}
      <header className="sticky top-0 z-[70]">
        <div className="bg-[#0f1729]/95 backdrop-blur-md border-b border-white/10">
          <div className="max-w-6xl mx-auto px-6 h-11 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {effectiveOrg?.logo ? (
                <img src={effectiveOrg.logo} alt="" className="w-6 h-6 object-contain rounded" />
              ) : (
                <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">{orgName.charAt(0)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white/90">{orgName}</span>
                <span className="text-white/20">|</span>
                <span className="text-xs text-white/40 font-medium">Training Portal</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ Hero (dark branded banner) ═══ */}
      <div className="relative bg-gradient-to-br from-[#0f1729] via-[#1a1f3a] to-[#0d1424] text-white overflow-hidden">
        {/* Decorative glows */}
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-cyan-500/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-violet-500/8 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] bg-indigo-500/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto px-6 py-8 relative z-10">
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1 min-w-0">
              {/* Org badge */}
              <div className="flex items-center gap-2.5 mb-4">
                {effectiveOrg?.logo ? (
                  <img src={effectiveOrg.logo} alt="" className="w-8 h-8 object-contain rounded-lg bg-white/10 p-0.5" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/10">
                    <span className="text-white text-sm font-bold">{orgName.charAt(0)}</span>
                  </div>
                )}
                <span className="text-xs font-semibold text-white/50 uppercase tracking-widest">Training Catalog</span>
              </div>

              <h1 className="text-2xl font-bold tracking-tight mb-1.5">
                {effectiveOrg?.company_name || orgName}
              </h1>
              <p className="text-sm text-slate-400 max-w-lg leading-relaxed">
                Browse upcoming events and register for sessions that match your learning goals.
              </p>

              {/* Search */}
              <div className="relative mt-5 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search events, courses..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-9 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white outline-none
                    placeholder:text-slate-500 focus:bg-white/10 focus:border-white/20 focus:ring-2 focus:ring-white/5 transition-all backdrop-blur-sm"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white bg-transparent border-none cursor-pointer p-0"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Stats cards (frosted glass) */}
            <div className="hidden md:grid grid-cols-3 gap-3 flex-shrink-0 pt-4">
              {[
                { value: total, label: 'Events', icon: Lightning, accent: 'text-cyan-400' },
                { value: totalSessions, label: 'Sessions', icon: Clock, accent: 'text-violet-400' },
                { value: totalSpots, label: 'Open Spots', icon: UsersIcon, accent: 'text-emerald-400' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[90px] text-center border border-white/10">
                  <stat.icon size={16} weight="bold" className={`${stat.accent} mx-auto mb-1`} />
                  <div className="text-xl font-bold text-white tabular-nums leading-tight">{stat.value}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Tabs + Filters bar ═══ */}
      <div className="sticky top-[51px] z-[60] bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Tabs */}
            <div className="flex -mb-px">
              {([
                { key: 'all' as TabKey, label: 'All Events' },
                { key: 'current' as TabKey, label: 'Upcoming' },
                { key: 'past' as TabKey, label: 'Past' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors bg-transparent cursor-pointer border-t-0 border-l-0 border-r-0
                    ${activeTab === tab.key
                      ? 'text-[#4B45E5] border-[#4B45E5]'
                      : 'text-gray-400 border-transparent hover:text-gray-600'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Filters + sort */}
            <div className="flex items-center gap-2">
              <FilterDropdown
                label="Format"
                icon={Monitor}
                items={[
                  { key: 'all' as ModeFilter, label: 'All Formats' },
                  { key: 'virtual' as ModeFilter, label: 'Virtual' },
                  { key: 'onsite' as ModeFilter, label: 'Onsite' },
                  { key: 'hybrid' as ModeFilter, label: 'Hybrid' },
                ]}
                value={modeFilter}
                onChange={setModeFilter}
              />
              <FilterDropdown
                label="When"
                icon={CalendarBlank}
                items={[
                  { key: 'all', label: 'Any Time' },
                  { key: 'this_month', label: 'This Month' },
                  { key: 'next_month', label: 'Next Month' },
                ]}
                value={timeFilter}
                onChange={setTimeFilter}
              />
              <FilterDropdown
                label="Sort"
                icon={SortAscending}
                items={[
                  { key: 'date_asc' as SortKey, label: 'Date: Soonest' },
                  { key: 'date_desc' as SortKey, label: 'Date: Latest' },
                  { key: 'popular' as SortKey, label: 'Most Popular' },
                  { key: 'spots' as SortKey, label: 'Most Spots' },
                ]}
                value={sortBy}
                onChange={setSortBy}
              />

              {/* Active filters + clear */}
              {hasActiveFilters && (
                <button
                  onClick={() => { setSearch(''); setModeFilter('all'); setTimeFilter('all'); }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer transition-colors"
                >
                  <X size={12} />
                  Clear
                </button>
              )}

              <div className="ml-2 pl-2 border-l border-gray-200 text-xs text-gray-400 font-medium tabular-nums whitespace-nowrap">
                {filtered.length} result{filtered.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Main Content ═══ */}
      <div className="flex-1 max-w-6xl mx-auto px-6 py-8 w-full">

        {/* Loading — skeleton cards */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="max-w-sm mx-auto my-16 p-8 text-center bg-white rounded-xl border border-red-100 shadow-sm">
            <WarningCircle size={28} weight="duotone" className="text-red-400 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700 mb-1">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs font-medium text-[#4B45E5] hover:underline bg-transparent border-none cursor-pointer mt-2"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
              <Tray size={36} weight="duotone" className="text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No events found</h3>
            <p className="text-sm text-gray-400 max-w-sm mx-auto leading-relaxed mb-5">
              {hasActiveFilters
                ? 'Try adjusting your search or filters to find what you\'re looking for.'
                : 'New training programs will appear here as they\'re published.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => { setSearch(''); setModeFilter('all'); setTimeFilter('all'); }}
                className="px-5 py-2.5 text-sm font-semibold text-[#4B45E5] bg-[#4B45E51A] hover:bg-[#4B45E533] rounded-lg border-none cursor-pointer transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Event Grid */}
        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((event, i) => (
              <EventCard
                key={event.id}
                event={event}
                isPast={isEventPast(event)}
                index={i}
                onClick={() => navigate(`/training/event/${event.event_id || event.id}/detail`)}
              />
            ))}
          </div>
        )}

        {/* Load More */}
        {!loading && !error && filtered.length > 0 && totalPages > 1 && page < totalPages - 1 && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl cursor-pointer
                hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
              Load more events
            </button>
          </div>
        )}
      </div>

      {/* ═══ Footer ═══ */}
      <footer className="border-t border-gray-100 mt-auto bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">
            &copy; {new Date().getFullYear()} {effectiveOrg?.company_name || 'Trainery'}
          </span>
          <span className="text-[11px] text-gray-300">
            Powered by Trainery
          </span>
        </div>
      </footer>

      {/* ═══ Animation keyframes ═══ */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
